import React, {useEffect, useState} from "react";

import {
  formatCurrency,
  formatSustainableAge
} from "./CalculatorLogic";
import {colors} from "nxtconstants";
import {
  CALC_ACCENT,
  CALC_SWP_PHASE,
  CompactField,
  CompactInput,
  CompactLabel,
  CompactValueBox,
  DataTable,
  LegendContainer,
  LegendItem,
  LegendLabel,
  LegendSwatch,
  MobileSummaryCard,
  MobileSummaryRow,
  MobileTabButton,
  MobileTabContainer,
  SummaryGrid,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  SustainSummary,
  TableWrapper,
  ViewToggleButton,
  ViewToggleContainer
} from "./CalculatorStyle";
import {
  TooltipContainer,
  TooltipRow,
  TooltipTitle,
  TooltipValue
} from "components/ClientHome/CalculatorStyle";

export const getPhaseColor = (phase) => {
  if (phase === "SWP") {
    return CALC_SWP_PHASE;
  }

  if (phase === "HOLD") {
    return CALC_ACCENT;
  }

  return colors.primary;
};

export const getPhaseLabel = (phase) => {
  if (phase === "SWP") {
    return "SWP Phase";
  }

  if (phase === "HOLD") {
    return "Holding Phase";
  }

  return "SIP Phase";
};

export const StackedInput = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix = "",
  suffix = ""
}) => {
  const formatDisplayValue = (nextValue) => {
    if (suffix === "%") {
      return (Math.round(nextValue * 2) / 2).toFixed(1);
    }

    if (suffix.includes("Years")) {
      return String(Math.round(nextValue));
    }

    return String(nextValue);
  };

  const [inputValue, setInputValue] = useState(formatDisplayValue(value));

  useEffect(() => {
    setInputValue(formatDisplayValue(value));
  }, [value, suffix]);

  const commitValue = () => {
    let nextValue = parseFloat(inputValue);

    if (Number.isNaN(nextValue)) {
      setInputValue(formatDisplayValue(value));
      return;
    }

    if (nextValue < min) {
      nextValue = min;
    }

    if (nextValue > max) {
      nextValue = max;
    }

    if (suffix.includes("Years")) {
      nextValue = Math.round(nextValue);
    } else if (suffix === "%") {
      nextValue = Math.round(nextValue * 2) / 2;
    } else if (prefix === "₹") {
      nextValue = Math.round(nextValue);
    }

    setInputValue(formatDisplayValue(nextValue));
    onChange(nextValue);
  };

  return (
    <CompactField>
      <CompactLabel>{label}</CompactLabel>
      <CompactValueBox>
        {prefix ? <span>{prefix}</span> : null}
        <CompactInput
          type="text"
          value={inputValue}
          onBlur={commitValue}
          onChange={(event) =>
            setInputValue(event.target.value.replace(/[^0-9.]/g, ""))
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
        />
        {suffix ? <span>{suffix}</span> : null}
      </CompactValueBox>
    </CompactField>
  );
};

export const MobileTabToggle = ({activeTab, onChange}) => (
  <MobileTabContainer>
    <MobileTabButton
      active={activeTab === "input"}
      type="button"
      onClick={() => onChange("input")}
    >
      Input
    </MobileTabButton>
    <MobileTabButton
      active={activeTab === "result"}
      type="button"
      onClick={() => onChange("result")}
    >
      Result
    </MobileTabButton>
  </MobileTabContainer>
);

export const SummaryCards = ({
  corpusBeforeSwp,
  corpusUsed,
  leftoverCorpus,
  sustainTillAge,
  swpStartAge
}) => (
  <>
    <SummaryGrid>
      <SummaryItem>
        <SummaryLabel>Corpus at Age {swpStartAge}</SummaryLabel>
        <SummaryValue>{formatCurrency(corpusBeforeSwp)}</SummaryValue>
      </SummaryItem>
      <SummaryItem>
        <SummaryLabel>Total Withdrawal</SummaryLabel>
        <SummaryValue>{formatCurrency(corpusUsed)}</SummaryValue>
      </SummaryItem>
      <SummaryItem>
        <SummaryLabel>Remaining Corpus</SummaryLabel>
        <SummaryValue>{formatCurrency(leftoverCorpus)}</SummaryValue>
      </SummaryItem>
    </SummaryGrid>

    <SustainSummary>
      Sustainable Till Age{" "}
      <strong>{formatSustainableAge(sustainTillAge)}</strong>
    </SustainSummary>

    <MobileSummaryCard>
      <MobileSummaryRow $highlight>
        <span>Corpus at age {swpStartAge}</span>
        <span>{formatCurrency(corpusBeforeSwp)}</span>
      </MobileSummaryRow>
      <MobileSummaryRow>
        <span>Total Withdrawal</span>
        <span>{formatCurrency(corpusUsed)}</span>
      </MobileSummaryRow>
      <MobileSummaryRow>
        <span>Remaining Corpus</span>
        <span>{formatCurrency(leftoverCorpus)}</span>
      </MobileSummaryRow>
      <MobileSummaryRow>
        <span>Sustainable Till Age</span>
        <span>{formatSustainableAge(sustainTillAge)}</span>
      </MobileSummaryRow>
    </MobileSummaryCard>
  </>
);

export const ViewToggle = ({activeView, onChange}) => (
  <ViewToggleContainer>
    <ViewToggleButton
      active={activeView === "graph"}
      type="button"
      onClick={() => onChange("graph")}
    >
      Graph
    </ViewToggleButton>
    <ViewToggleButton
      active={activeView === "table"}
      type="button"
      onClick={() => onChange("table")}
    >
      Table
    </ViewToggleButton>
  </ViewToggleContainer>
);

export const YearlyTable = ({yearlyData}) => (
  <TableWrapper>
    <DataTable>
      <thead>
        <tr>
          <th>Age</th>
          <th>Year Beginning Corpus</th>
          <th>Monthly SIP</th>
          <th>Yearly SIP</th>
          <th>Monthly Withdrawal</th>
          <th>Yearly Withdrawal</th>
          <th>Year End Corpus</th>
        </tr>
      </thead>
      <tbody>
        {yearlyData.map((row) => (
          <tr key={`${row.yearLabel}-${row.phase}`}>
            <td>{row.yearLabel}</td>
            <td>{formatCurrency(row.yearStartCorpus)}</td>
            <td>{formatCurrency(row.monthlySip)}</td>
            <td>{formatCurrency(row.yearlySip)}</td>
            <td>{formatCurrency(row.monthlyWithdrawal)}</td>
            <td>{formatCurrency(row.yearlyWithdrawal)}</td>
            <td>{formatCurrency(row.Corpus)}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  </TableWrapper>
);

export const ChartHoverInfo = ({active, payload, label}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const corpus = payload[0].value;
  const phase = payload[0]?.payload?.phase;

  return (
    <TooltipContainer>
      <TooltipTitle>Age {label}</TooltipTitle>
      <TooltipRow color="#111827">
        {getPhaseLabel(phase)}{" "}
        <TooltipValue style={{color: "#111827"}}>{formatCurrency(corpus)}</TooltipValue>
      </TooltipRow>
    </TooltipContainer>
  );
};

export const ChartLabel = () => (
  <LegendContainer>
    <LegendItem>
      <LegendSwatch color={colors.primary} />
      <LegendLabel>SIP Phase</LegendLabel>
    </LegendItem>
    <LegendItem>
      <LegendSwatch color={CALC_ACCENT} />
      <LegendLabel>Holding Phase</LegendLabel>
    </LegendItem>
    <LegendItem>
      <LegendSwatch color={CALC_SWP_PHASE} />
      <LegendLabel>SWP Phase</LegendLabel>
    </LegendItem>
  </LegendContainer>
);
